import { useState } from "react";
import { Music2 } from "lucide-react";

const TABS = [
  { id: "spotify", label: "Spotify", color: "#1DB954" },
  { id: "apple", label: "Apple Music", color: "#FA243C" },
  { id: "youtube", label: "YouTube Music", color: "#FF0000" },
];

const Cover = ({ src, className }) => src
  ? <img src={src} alt="" className={`${className} object-cover`} />
  : <div className={`${className} flex items-center justify-center bg-gradient-to-br from-[#4A080C] to-[#120404]`}><Music2 className="h-10 w-10 text-white/30" /></div>;

const Bar = ({ color, pct = 38, dark = true }) => (
  <div className={`h-1 w-full rounded-full ${dark ? "bg-white/20" : "bg-black/15"}`}><div className="h-1 rounded-full" style={{ width: `${pct}%`, background: color }} /></div>
);

const Controls = ({ color, fill = "white" }) => (
  <div className="flex items-center justify-between px-2" style={{ color: fill }}>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" opacity=".7"><path d="M17 17H7v-3l-4 4 4 4v-3h12v-6h-2zM7 7h10v3l4-4-4-4v3H5v6h2z" /></svg>
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h2v12H6zm3.5 6 8.5 6V6z" /></svg>
    <span className="flex h-12 w-12 items-center justify-center rounded-full" style={{ background: color === "#FFFFFF" ? "white" : color }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill={color === "#FFFFFF" ? "#000" : "#fff"}><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
    </span>
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="m6 18 8.5-6L6 6v12zM16 6v12h2V6h-2z" /></svg>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" opacity=".7"><path d="M10.59 9.17 5.41 4 4 5.41l5.17 5.17 1.42-1.41zM14.5 4l2.04 2.04L4 18.59 5.41 20 17.96 7.46 20 9.5V4h-5.5zm.33 9.41-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z" /></svg>
  </div>
);

function SpotifyUI({ cover, title, artists }) {
  return (
    <div className="flex h-full flex-col bg-gradient-to-b from-[#3b2a2a] via-[#121212] to-[#121212] px-5 pb-6 pt-12 text-white">
      <div className="flex items-center justify-between text-[10px] opacity-70"><span>⌄</span><span className="font-semibold uppercase tracking-wider">Reproduciendo</span><span>···</span></div>
      <Cover src={cover} className="mt-6 aspect-square w-full rounded-lg shadow-2xl" />
      <div className="mt-6 min-w-0">
        <div className="truncate text-lg font-bold" data-testid="preview-title">{title}</div>
        <div className="truncate text-sm text-white/60" data-testid="preview-artists">{artists}</div>
      </div>
      <div className="mt-4"><Bar color="#fff" /><div className="mt-1 flex justify-between text-[9px] text-white/50"><span>1:12</span><span>-2:04</span></div></div>
      <div className="mt-4"><Controls color="#FFFFFF" /></div>
    </div>
  );
}

function AppleUI({ cover, title, artists }) {
  return (
    <div className="relative flex h-full flex-col overflow-hidden px-5 pb-6 pt-12 text-white">
      {cover ? <img src={cover} alt="" className="absolute inset-0 h-full w-full scale-150 object-cover blur-3xl brightness-75" /> : <div className="absolute inset-0 bg-gradient-to-b from-[#6b1b20] to-[#1a0507]" />}
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative flex flex-col">
        <div className="mx-auto h-1 w-9 rounded-full bg-white/40" />
        <Cover src={cover} className="mt-8 aspect-square w-full rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.6)]" />
        <div className="mt-7 flex items-start justify-between gap-2">
          <div className="min-w-0"><div className="truncate text-lg font-semibold" data-testid="preview-title">{title}</div><div className="truncate text-sm text-white/70" data-testid="preview-artists">{artists}</div></div>
          <span className="rounded-full bg-white/20 px-2 py-1 text-[10px] backdrop-blur">···</span>
        </div>
        <div className="mt-4"><Bar color="rgba(255,255,255,.9)" pct={44} /></div>
        <div className="mt-5"><Controls color="#FFFFFF" /></div>
        <div className="mt-5 flex items-center gap-2 text-white/60"><span className="text-[10px]">🔈</span><Bar color="rgba(255,255,255,.8)" pct={60} /><span className="text-[10px]">🔊</span></div>
      </div>
    </div>
  );
}

function YouTubeUI({ cover, title, artists }) {
  return (
    <div className="flex h-full flex-col bg-[#030303] px-5 pb-6 pt-12 text-white">
      <div className="flex items-center justify-between text-[10px] text-white/70"><span>⌄</span><div className="flex gap-3 rounded-full bg-white/10 px-3 py-1"><span className="font-semibold text-white">CANCIÓN</span><span>VIDEO</span></div><span>···</span></div>
      <Cover src={cover} className="mt-8 aspect-square w-full rounded-md shadow-2xl" />
      <div className="mt-6 text-center">
        <div className="truncate text-lg font-semibold" data-testid="preview-title">{title}</div>
        <div className="truncate text-sm text-white/60" data-testid="preview-artists">{artists}</div>
      </div>
      <div className="mt-3 flex justify-center gap-5 text-[10px] text-white/60"><span>👍 Me gusta</span><span>💬</span><span>Guardar</span><span>↗ Compartir</span></div>
      <div className="mt-4"><Bar color="#fff" pct={30} /><div className="mt-1 flex justify-between text-[9px] text-white/50"><span>0:58</span><span>3:16</span></div></div>
      <div className="mt-4"><Controls color="#FFFFFF" /></div>
    </div>
  );
}

export function PhonePreview({ cover, title, version, artists, className = "" }) {
  const [tab, setTab] = useState("spotify");
  const fullTitle = `${title?.trim() || "Título del lanzamiento"}${version?.trim() ? ` - ${version.trim()}` : ""}`;
  const art = artists?.trim() || "Artista principal";
  const UI = { spotify: SpotifyUI, apple: AppleUI, youtube: YouTubeUI }[tab];
  const active = TABS.find((t) => t.id === tab);
  return (
    <div className={`relative overflow-hidden rounded-3xl p-5 aura-bg aura-waves ${className}`} data-testid="phone-preview">
      <div className="relative">
        <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/60">Previsualización Live DSP</div>
        <div className="mt-3 flex rounded-full bg-white/10 p-1 backdrop-blur" data-testid="preview-tabs">
          {TABS.map((t) => (
            <button key={t.id} type="button" data-testid={`preview-tab-${t.id}`} onClick={() => setTab(t.id)} className={`flex-1 rounded-full px-2 py-1.5 text-[10px] font-semibold transition-all ${tab === t.id ? "bg-white text-black shadow" : "text-white/70 hover:text-white"}`}>{t.label}</button>
          ))}
        </div>
        <div className="mx-auto mt-6 w-[250px]">
          <div className="relative rounded-[40px] border-[6px] border-[#1c1c1e] bg-black shadow-[0_30px_70px_-20px_rgba(0,0,0,0.9),inset_0_0_0_1.5px_rgba(255,255,255,0.08)]" style={{ boxShadow: `0 30px 70px -20px rgba(0,0,0,.9), 0 0 60px -20px ${active.color}` }}>
            <div className="absolute left-1/2 top-2.5 z-10 h-6 w-20 -translate-x-1/2 rounded-full bg-black ring-1 ring-white/5" />
            <div className="absolute -left-[8px] top-24 h-7 w-[3px] rounded-l bg-[#2a2a2c]" /><div className="absolute -left-[8px] top-36 h-12 w-[3px] rounded-l bg-[#2a2a2c]" /><div className="absolute -right-[8px] top-32 h-16 w-[3px] rounded-r bg-[#2a2a2c]" />
            <div className="relative h-[520px] overflow-hidden rounded-[34px]">
              <UI cover={cover} title={fullTitle} artists={art} />
              <div className="absolute bottom-2 left-1/2 h-1 w-24 -translate-x-1/2 rounded-full bg-white/60" />
            </div>
          </div>
        </div>
        <p className="mt-5 text-center text-[11px] text-white/50">Así se verá tu lanzamiento en {active.label}. Se actualiza en tiempo real.</p>
      </div>
    </div>
  );
}
